import React from 'react';
import { ObjectModal as Props } from './index.types';
import {
  Alert,
  Checkbox,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Button from 'src/Lca/components/Button';

import {
  StyledTitle,
  StyledSectionName,
  StyledNumberField,
  StyledTextField,
  StyledRow,
  StyledLine,
  StyledSelect,
} from './index.styles';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { client } from 'src/utils/api-client';
import { AxiosError } from 'axios';
import {
  EcoinventActivity,
  LCAProcess,
  LcaObjectCustomFactor,
} from '../index.types';
import { parseRequestErrors } from 'src/utils/helpers';
import ActivitiesSearchField from '../components/ActivitiesSearchField';
import {
  useEcoinventProLicenceValid,
  useLcaDiagram,
  useLcaProduct,
} from 'src/Lca/hooks';
import ConfirmationModal from 'src/components/ConfirmationModal';
import { toast } from 'react-toastify';
import { useLcaObjects } from '../hooks';
import CustomFactorsFields from './CustomFactorsFields';
import { useTranslation } from 'react-i18next';
import GlnDistanceDialog from 'src/components/GlnDistanceDialog';
import { parseNullableDecimal } from 'src/Lca/utils/number';
import { getLifecycleSubcategoryLabel } from 'src/Lca/LCADiagram/labels';
import {
  createAmountBreakdownDraft,
  getAmountBreakdownTotal,
  getEnergyConversionTotal,
  getTransportDistanceBreakdownIndex,
  isEnergyConversionUnit,
  parseCompositeUnit,
  createEnergyConversionDraft,
  toEnergyConversionPayload,
  toAmountBreakdownPayload,
  type AmountBreakdownDraftPart,
  type EnergyConversionDraft,
} from '../compositeUnits';
import shouldShowGlnCalculator from './Object.utils';

export default function ObjectModal({
  productId,
  open,
  onClose: _onClose,
  object,
  subcategory,
}: Props) {
  const isNew = !object?.id;
  const url = `/web/lca/objects/${productId}`;

  type InputType = string | undefined;
  type InputEvent = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

  const [categoryKey, setCategoryKey] = React.useState('');

  const { product } = useLcaProduct(productId);
  const { stagesAsOptions } = useLcaDiagram(productId);
  const { isEcoinventLicenceValid } = useEcoinventProLicenceValid();
  const { t } = useTranslation('lca', { keyPrefix: 'diagram' });
  const { t: tBase } = useTranslation('lca');
  const ecoinventVersion = product?.ecoinvent_version;
  const selectedSubcategoryRawId = subcategory?.id ?? object?.subcategory;
  const selectedSubcategoryId =
    selectedSubcategoryRawId !== undefined && selectedSubcategoryRawId !== null
      ? Number(selectedSubcategoryRawId)
      : null;

  const [showGlnCodesCalculator, setShowGlnCodesCalculator] =
    React.useState<boolean>(false);

  React.useEffect(() => {
    if (object?.category_key) setCategoryKey(object?.category_key);
    else if (object?.category_key === null) setCategoryKey('uncategorized');
  }, [object]);

  const getProcesses = useQuery(`lca-product-${productId}-processes`, () =>
    client
      .get(`/web/lca/processes/${productId}`)
      .then((response) => response.data)
  );

  const [name, setName] = React.useState<InputType>('');
  const [ecoinventActivity, setEcoinventActivity] = React.useState<
    number | null
  >(null);
  const [amount, setAmount] = React.useState<InputType>('');
  const [amountBreakdown, setAmountBreakdown] = React.useState<
    AmountBreakdownDraftPart[]
  >([]);
  const [useAmountBreakdown, setUseAmountBreakdown] =
    React.useState<boolean>(false);
  const [energyConversion, setEnergyConversion] =
    React.useState<EnergyConversionDraft>(createEnergyConversionDraft());
  const [useEnergyConversion, setUseEnergyConversion] =
    React.useState<boolean>(false);
  const [glnCodes, setGlnCodes] = React.useState<string[]>([]);
  const [glnCodesMultiplier, setGlnCodesMultiplier] = React.useState<number>(1);
  const [unit, setUnit] = React.useState<InputType>('');
  const [comment, setComment] = React.useState<InputType>('');
  const [stage, setStage] = React.useState<InputType>('');
  const [process, setProcess] = React.useState<InputType>('');
  const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
  const [useCustomFactors, setUseCustomFactors] =
    React.useState<boolean>(false);
  const [customFactorValues, setCustomFactorValues] = React.useState<
    LcaObjectCustomFactor[]
  >([]);

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] =
    React.useState<boolean>(false);

  const clearAmountErrors = () => {
    setErrors((prevErrors) => {
      const nextErrors = { ...prevErrors };
      delete nextErrors.amount;
      delete nextErrors.amount_breakdown;
      delete nextErrors.energy_conversion;
      return nextErrors;
    });
  };

  const handleNameChange = (e: InputEvent) => setName(e.target.value);
  const handleAmountChange = (value: string | number | null) => {
    clearAmountErrors();
    setAmount(value?.toString() ?? '');
    setGlnCodes([]);
    setGlnCodesMultiplier(1);
  };
  const handleAmountBreakdownChange = (
    index: number,
    value: string | number | null
  ) => {
    clearAmountErrors();
    const isDistancePart = amountBreakdown[index]?.unit === 'km';
    setAmountBreakdown((prevValue) =>
      prevValue.map((part, partIndex) =>
        partIndex === index ? { ...part, value: value?.toString() ?? '' } : part
      )
    );
    if (isDistancePart) {
      setGlnCodes([]);
      setGlnCodesMultiplier(1);
    }
  };
  const handleEnergyConversionChange = (
    field: keyof EnergyConversionDraft,
    value: string | number | null
  ) => {
    clearAmountErrors();
    setEnergyConversion((prevValue) => ({
      ...prevValue,
      [field]: value?.toString() ?? '',
    }));
  };
  const handleUnitChange = () => {};
  const handleCommentChange = (e: InputEvent) => setComment(e.target.value);
  const handleUseCustomFactorsChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (object?.custom_factors && object?.custom_factors.length > 0) {
      return;
    }
    setUseCustomFactors(e.target.checked);
  };
  const handleUseAmountBreakdownChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextValue = e.target.checked;
    clearAmountErrors();
    if (!nextValue && amountBreakdownTotal !== null) {
      setAmount(String(amountBreakdownTotal));
    }
    if (nextValue && !amountBreakdown.length && compositeUnitParts.length > 0) {
      setAmountBreakdown(createAmountBreakdownDraft(unit));
    }
    if (nextValue) {
      setUseEnergyConversion(false);
    }
    setUseAmountBreakdown(nextValue);
  };
  const handleUseEnergyConversionChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextValue = e.target.checked;
    clearAmountErrors();
    if (!nextValue && energyConversionTotal !== null) {
      setAmount(String(energyConversionTotal));
    }
    if (nextValue) {
      setUseAmountBreakdown(false);
    }
    setUseEnergyConversion(nextValue);
  };

  const handleProcessChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const { value: newProcess } = e.target;
    setProcess(newProcess as string);
    const process = processOptions?.find(
      (process: LCAProcess) => String(process.id) === newProcess
    );
    if (process) setStage(process.stage);
  };

  const handleActivitySearchChange = (activity: EcoinventActivity | null) => {
    const nextUnit = activity?.reference_product_unit ?? '';
    const nextActivityId = activity?.id ?? null;
    if (nextUnit !== unit || nextActivityId !== ecoinventActivity) {
      clearAmountErrors();
      setAmount('');
      setGlnCodes([]);
      setGlnCodesMultiplier(1);
      const nextAmountBreakdown = createAmountBreakdownDraft(nextUnit);
      setAmountBreakdown(nextAmountBreakdown);
      setEnergyConversion(createEnergyConversionDraft());
      setUseAmountBreakdown(false);
      setUseEnergyConversion(false);
    }
    setEcoinventActivity(activity?.id ?? null);
    setUnit(nextUnit);
  };

  const handleStageChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const { value: newStage } = e.target;
    const isProcessInStage = !!processOptions?.filter(
      (p: LCAProcess) => p.id === Number(process)
    )?.length;
    if (!isProcessInStage) setProcess('');
    setStage(newStage as string);
  };

  const handleCustomFactorChange = (e: InputEvent, id: number) => {
    const { value } = e.target;
    const factor = parseNullableDecimal(value);
    setCustomFactorValues((prevValues) => {
      // Filter out the previous value for the given id and add the new one
      return prevValues
        .filter((item) => item.area_id !== id)
        .concat({ area_id: id, factor });
    });
  };

  const onClose = (e?: any) => {
    e?.preventDefault();
    setName('');
    setAmount('');
    setAmountBreakdown([]);
    setUseAmountBreakdown(false);
    setEnergyConversion(createEnergyConversionDraft());
    setUseEnergyConversion(false);
    setGlnCodes([]);
    setGlnCodesMultiplier(1);
    setUnit('');
    setComment('');
    setStage('');
    setProcess('');
    setEcoinventActivity(null);
    setErrors({});
    _onClose();
    setCustomFactorValues([]);
    setShowGlnCodesCalculator(false);
  };

  const processOptions = getProcesses.data?.filter(
    (process: LCAProcess) => !stage || process.stage === stage
  );
  const selectedProcessValue = processOptions?.some(
    (candidate: LCAProcess) => String(candidate.id) === process
  )
    ? process
    : '';

  React.useEffect(() => {
    if (!isNew) {
      const objectUnit =
        object?.ecoinvent_activity?.reference_product_unit || '';
      setName(String(object.name));
      setAmount(String(object.amount));
      setGlnCodes(object?.gln_codes || []);
      setGlnCodesMultiplier(object?.gln_codes_multiplier || 1);
      setComment(String(object?.comment));
      setStage(String(object.stage));
      setProcess(String(object?.parent_process || ''));
      setEcoinventActivity(object?.ecoinvent_activity?.id || null);
      setUnit(objectUnit);
      setAmountBreakdown(
        createAmountBreakdownDraft(objectUnit, object?.amount_breakdown || null)
      );
      setUseAmountBreakdown(!!object?.amount_breakdown?.length);
      setEnergyConversion(
        createEnergyConversionDraft(object?.energy_conversion || null)
      );
      setUseEnergyConversion(!!object?.energy_conversion);
      setCustomFactorValues(object?.custom_factors || []);
      if (object?.custom_factors && object?.custom_factors.length > 0) {
        setUseCustomFactors(true);
      }
    }
  }, []);

  const queryClient = useQueryClient();

  const requestActionshandlers = {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['lca-diagram'],
      });
      setErrors({});
      onClose();
    },
    onError: (error: AxiosError) =>
      setErrors(parseRequestErrors(error?.response?.data)),
  };

  const postObject = useMutation(
    (data: { [key: string]: any }) => client.post(url, data),
    requestActionshandlers
  );

  const putObject = useMutation(
    (data: { [key: string]: any }) => client.put(`${url}/${object?.id}`, data),
    requestActionshandlers
  );
  const deleteObject = useMutation(
    () => client.delete(`${url}/${object?.id}`),
    requestActionshandlers
  );

  const handleSubmit = () => {
    const amountBreakdownPayload = useAmountBreakdown
      ? toAmountBreakdownPayload(amountBreakdown)
      : null;
    const amountBreakdownTotal = useAmountBreakdown
      ? getAmountBreakdownTotal(amountBreakdown)
      : null;
    const energyConversionPayload = useEnergyConversion
      ? toEnergyConversionPayload(energyConversion)
      : null;
    const energyConversionTotal = useEnergyConversion
      ? getEnergyConversionTotal(energyConversion)
      : null;

    if (
      useAmountBreakdown &&
      (!amountBreakdownPayload || amountBreakdownTotal === null)
    ) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        amount_breakdown: t('amountBreakdownRequired') as string,
      }));
      return;
    }

    if (
      useEnergyConversion &&
      (!energyConversionPayload || energyConversionTotal === null)
    ) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        energy_conversion: t('energyConversionRequired') as string,
      }));
      return;
    }

    let resolvedAmount = amount;

    if (useAmountBreakdown) {
      resolvedAmount =
        amountBreakdownTotal === null ? '' : String(amountBreakdownTotal);
    } else if (useEnergyConversion) {
      resolvedAmount =
        energyConversionTotal === null ? '' : String(energyConversionTotal);
    }

    const data = {
      name,
      amount: resolvedAmount,
      amount_breakdown: amountBreakdownPayload,
      energy_conversion: energyConversionPayload,
      gln_codes: glnCodes,
      gln_codes_multiplier: glnCodesMultiplier,
      comment,
      stage,
      parent_process_id: process || undefined,
      product_id: productId,
      subcategory_id: subcategory?.id,
      ecoinvent_activity_id: ecoinventActivity,
      custom_factor_values: customFactorValues,
    };
    if (isNew) {
      postObject.mutate(data);
    } else {
      if (!isEcoinventLicenceValid && !!ecoinventActivity) {
        toast.error(t('noValidLicenceNoEdit') as string);
        return;
      }
      putObject.mutate(data);
    }
  };

  const handleDeleteObject = () => {
    setDeleteConfirmationOpen(false);
    deleteObject.mutateAsync().then(() => {
      toast.success(t('objectRemoved') as string);
    });
  };
  const { requestCopy } = useLcaObjects();
  const handleCopyClick = () => !!object?.id && requestCopy.mutate(object.id);

  const saveButtonText = isNew ? t('create') : t('saveChanges');

  const isLoading = getProcesses.isLoading || postObject.isLoading;

  const compositeUnitParts = parseCompositeUnit(unit);
  const isEnergyUnit = isEnergyConversionUnit(unit);
  const shouldUseSplitAmount =
    compositeUnitParts.length > 0 && useAmountBreakdown;
  const shouldUseEnergyCalculator = isEnergyUnit && useEnergyConversion;
  const amountBreakdownTotal = shouldUseSplitAmount
    ? getAmountBreakdownTotal(amountBreakdown)
    : null;
  const energyConversionTotal = shouldUseEnergyCalculator
    ? getEnergyConversionTotal(energyConversion)
    : null;
  const distanceBreakdownIndex = getTransportDistanceBreakdownIndex(
    unit,
    amountBreakdown
  );
  const showCalculationButton = shouldShowGlnCalculator({
    distanceBreakdownIndex,
    shouldUseSplitAmount,
    stage,
    unit,
  });
  let displayedAmount = amount;
  if (shouldUseSplitAmount) {
    displayedAmount =
      amountBreakdownTotal === null ? '' : String(amountBreakdownTotal);
  } else if (shouldUseEnergyCalculator) {
    displayedAmount =
      energyConversionTotal === null ? '' : String(energyConversionTotal);
  }
  const subcategoryLabel = subcategory
    ? getLifecycleSubcategoryLabel(tBase, subcategory.name, subcategory.name)
    : tBase('node.uncategorized');

  return (
    <Dialog onClose={onClose} open={open} maxWidth="lg">
      <GlnDistanceDialog
        open={showGlnCodesCalculator}
        initialGlnCodes={glnCodes}
        initialGlnCodesMultiplier={glnCodesMultiplier}
        onCancel={() => setShowGlnCodesCalculator(false)}
        onSubmit={(
          glnCodes: string[],
          glnCodesMultiplier: number,
          distance: number
        ) => {
          setShowGlnCodesCalculator(false);
          setGlnCodes(glnCodes);
          setGlnCodesMultiplier(glnCodesMultiplier);
          if (shouldUseSplitAmount && distanceBreakdownIndex >= 0) {
            setAmountBreakdown((prevValue) =>
              prevValue.map((part, index) =>
                index === distanceBreakdownIndex
                  ? { ...part, value: distance.toFixed(0) }
                  : part
              )
            );
            return;
          }
          setAmount(distance.toFixed(0));
        }}
      />
      <IconButton
        sx={{ position: 'absolute', top: 12, right: 10 }}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>

      <StyledTitle>{subcategoryLabel}</StyledTitle>

      <DialogContent sx={{ width: 900 }}>
        <StyledSectionName>{t('materialDetails')}</StyledSectionName>
        <StyledRow>
          <StyledTextField
            fullWidth
            name="name"
            value={name}
            onChange={handleNameChange}
            disabled={isLoading}
            error={!!errors.name}
            helperText={errors.name}
            label={t('nameOfBuildingOrConstructionMaterial')}
            categorykey={categoryKey}
          />
        </StyledRow>
        <StyledRow sx={{ gap: '24px' }}>
          <ActivitiesSearchField
            value={ecoinventActivity}
            onChange={handleActivitySearchChange}
            object={object}
            ecoinventVersion={ecoinventVersion}
            productId={productId}
            subcategoryId={selectedSubcategoryId}
            isEcoinventLicenceValid={!!isEcoinventLicenceValid}
            preferredGeography={product?.production_country?.iso_code}
          />
        </StyledRow>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('ecoinventVersion')}: {ecoinventVersion}
        </Typography>
        {!isEcoinventLicenceValid && (
          <Alert severity="info">{t('provideCustomFactors')}</Alert>
        )}
        <StyledRow style={{ justifyContent: 'start' }}>
          <Checkbox
            checked={useCustomFactors}
            onChange={handleUseCustomFactorsChange}
          />
          <StyledSectionName>{t('useCustomFactors')}</StyledSectionName>
        </StyledRow>
        {useCustomFactors && (
          <>
            <CustomFactorsFields
              values={customFactorValues}
              handleChange={handleCustomFactorChange}
              errors={errors}
              isLoading={isLoading}
            />
            <StyledLine />
          </>
        )}

        <StyledRow>
          <StyledNumberField
            fullWidth
            name="amount"
            type="text"
            inputProps={{ inputMode: 'decimal' }}
            value={displayedAmount}
            onChange={handleAmountChange}
            disabled={
              isLoading || shouldUseSplitAmount || shouldUseEnergyCalculator
            }
            error={
              !shouldUseSplitAmount &&
              !shouldUseEnergyCalculator &&
              !!errors.amount
            }
            helperText={
              !shouldUseSplitAmount && !shouldUseEnergyCalculator
                ? errors.amount
                : undefined
            }
            label={t('amount')}
            categorykey={categoryKey}
          />
          <div style={{ width: 20 }} />
          <StyledTextField
            fullWidth
            name="unit"
            value={unit}
            onChange={handleUnitChange}
            disabled
            error={!!errors.unit}
            helperText={errors.unit}
            label={t('unit')}
            categorykey={categoryKey}
          />
          {!shouldUseSplitAmount && showCalculationButton && (
            <>
              <div style={{ width: 20 }} />
              <Button
                onClick={() => setShowGlnCodesCalculator(true)}
                color="default"
                isSecondary
              >
                {t('glnCodesCalculator')}
              </Button>
            </>
          )}
        </StyledRow>
        {compositeUnitParts.length > 0 && (
          <StyledRow style={{ justifyContent: 'start' }}>
            <Checkbox
              checked={useAmountBreakdown}
              onChange={handleUseAmountBreakdownChange}
              disabled={isLoading}
            />
            <StyledSectionName>
              {t('enterAmountSeparatelyByUnit')}
            </StyledSectionName>
          </StyledRow>
        )}
        {isEnergyUnit && (
          <StyledRow style={{ justifyContent: 'start' }}>
            <Checkbox
              checked={useEnergyConversion}
              onChange={handleUseEnergyConversionChange}
              disabled={isLoading}
            />
            <StyledSectionName>{t('energyCalculatorToggle')}</StyledSectionName>
          </StyledRow>
        )}
        {shouldUseSplitAmount && (
          <StyledRow style={{ gap: '20px', alignItems: 'flex-start' }}>
            {amountBreakdown.map((part, index) => (
              <StyledNumberField
                key={part.unit}
                fullWidth
                name={`amount_breakdown_${index}`}
                type="text"
                inputProps={{ inputMode: 'decimal' }}
                value={part.value}
                onChange={(value) => handleAmountBreakdownChange(index, value)}
                disabled={isLoading}
                error={!!errors.amount_breakdown}
                helperText={index === 0 ? errors.amount_breakdown : undefined}
                label={part.unit}
                categorykey={categoryKey}
              />
            ))}
            {showCalculationButton && (
              <Button
                onClick={() => setShowGlnCodesCalculator(true)}
                color="default"
                isSecondary
              >
                {t('glnCodesCalculator')}
              </Button>
            )}
          </StyledRow>
        )}
        {shouldUseEnergyCalculator && (
          <>
            <StyledRow style={{ gap: '20px', alignItems: 'flex-start' }}>
              <StyledNumberField
                fullWidth
                name="energy_conversion_source_amount"
                type="text"
                inputProps={{ inputMode: 'decimal' }}
                value={energyConversion.source_amount}
                onChange={(value) =>
                  handleEnergyConversionChange('source_amount', value)
                }
                disabled={isLoading}
                error={!!errors.energy_conversion}
                helperText={errors.energy_conversion}
                label={t('fuelAmount')}
                categorykey={categoryKey}
              />
              <StyledNumberField
                fullWidth
                name="energy_conversion_rate"
                type="text"
                inputProps={{ inputMode: 'decimal' }}
                value={energyConversion.energy_per_source_unit}
                onChange={(value) =>
                  handleEnergyConversionChange('energy_per_source_unit', value)
                }
                disabled={isLoading}
                error={false}
                label={t('energyPerFuelUnit', { unit })}
                categorykey={categoryKey}
              />
            </StyledRow>
            <Typography
              component="a"
              variant="caption"
              href="https://www.kobize.pl/uploads/materialy/materialy_do_pobrania/aktualnosci/2025/135.WO_i_WE_do_monitorowania_ETS_2026.pdf"
              target="_blank"
              rel="noreferrer"
              sx={{ color: 'text.secondary' }}
            >
              {t('kobizeEnergyConversionHint')}
            </Typography>
          </>
        )}

        <StyledRow>
          <StyledTextField
            fullWidth
            multiline
            minRows={2}
            name="comment"
            inputProps={{ maxLength: 1000 }}
            value={comment}
            onChange={handleCommentChange}
            disabled={isLoading}
            error={!!errors.comment}
            helperText={errors.comment}
            label={t('optionalComment')}
            categorykey={categoryKey}
          />
        </StyledRow>

        <StyledLine />

        <StyledSectionName>{t('lifeCyclePhases')}</StyledSectionName>
        <StyledRow>
          <StyledSelect
            fullWidth
            name="stage"
            value={stage}
            onChange={handleStageChange}
            disabled={isLoading}
            error={!!errors.stage}
            label={t('lifeCyclePhase')}
            categorykey={categoryKey}
          >
            <MenuItem value="">
              <em>{t('none')}</em>
            </MenuItem>
            {stagesAsOptions.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </StyledSelect>
          <div style={{ width: 20 }} />
          <StyledSelect
            fullWidth
            name="process"
            value={selectedProcessValue}
            onChange={handleProcessChange}
            disabled={isLoading}
            error={errors.parent_process_id}
            label={t('optionalAddToProcess')}
            categorykey={categoryKey}
          >
            <MenuItem value="">
              <em>{t('none')}</em>
            </MenuItem>
            {processOptions?.map((process: LCAProcess) => (
              <MenuItem key={process.id} value={process.id}>
                {process.name}
              </MenuItem>
            ))}
          </StyledSelect>
        </StyledRow>
        <br />

        {/* ACTION BUTTONS */}

        <StyledRow>
          {!!isNew && (
            <Button color="default" onClick={onClose} isSecondary>
              {t('cancel')}
            </Button>
          )}

          {!isNew && (
            <StyledRow style={{ padding: 0, justifyContent: 'flex-start' }}>
              <Button
                color="default"
                isSecondary
                onClick={() => setDeleteConfirmationOpen(true)}
              >
                {t('remove')}
              </Button>
              <div style={{ width: 10 }} />
              <Button color="default" onClick={handleCopyClick} isSecondary>
                {t('copy')}
              </Button>
              <div style={{ width: 10 }} />
              <Button color="default" onClick={onClose} isSecondary>
                {t('hide')}
              </Button>
            </StyledRow>
          )}

          <Button color={String(categoryKey)} onClick={handleSubmit}>
            {saveButtonText}
          </Button>
        </StyledRow>
      </DialogContent>
      <ConfirmationModal
        open={deleteConfirmationOpen}
        onClose={() => setDeleteConfirmationOpen(false)}
        onFalse={() => setDeleteConfirmationOpen(false)}
        onTrue={handleDeleteObject}
        textKey={t('removeConfirmationText')}
        titleKey={t('removeConfirmationTitle')}
      />
    </Dialog>
  );
}
